import useActivity from '@behavior/activity/getActivity';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { useTranslation, withTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import { Loading } from 'react-lib/dist/components-es';
import { useState, useEffect } from 'react';
import styles from '../components/styles/Activity.module.scss';
import { Form } from 'semantic-ui-react';
import TitleContainer from '../components/TitleContainer';
import RiskAssessmentDetailsContainer from '../components/riskAssessment/RiskAssessmentDetailsContainer';
import TraceabilityFormContainer from '../components/traceability/TraceabilityFormContainer';
import ActivityDetailsContainer from '../ActivityDetailsContainer';
import OptionsButtonGroup from './OptionsButtonGroup';
import {CLONE_MODE, EDIT_MODE, entityType, VIEW_MODE} from '@src/constants';
import { useActivityState } from '../useActivityState';
import useActivityInitialData from '@behavior/activity/getActivityInitialData';
import {
  updateActionStateFromAction,
  updateActivityStateFromActivity,
  updateTraceabilityStateFromTraceability,
} from '../activityUtils';
import { useRiskAssessmentState } from '../components/riskAssessment/useRiskAssessmentState';
import { updateRiskAssessmentStateFromRisk } from '../activityUtils';
import { useActionState } from '../components/actionForm/useActionState';
import ActionFormContainer from '../components/actionForm/ActionFormContainer';
import { useTraceabilityState } from '../components/traceability/useTraceabilityState';
import useRiskAssessmentInitialData from '@behavior/activity/getRiskAssessmentInitialData';
import useTraceabilityInitialData from '@behavior/activity/getTraceabilityInitialData';
import { QueryKey } from '@behavior/QueryKey';
import { useHistory } from 'react-router';
import { ActivityStatus } from '../types/ActivityStatus';
import {initSelectors} from "@behavior/init";
import useUser from '@behavior/init/getUserV2';

type Props = {
  id: number;
  mode: string;
};

const ViewEditAcivity = ({ id, mode }: Props) => {
  const history = useHistory();
  const { t } = useTranslation();
  const { activity, isFetching: isFetchingActivity } = useActivity(id);
  const methods = useForm({ mode: 'all' });
  const [isLoading, setIsLoading] = useState(false);
  const [createUserId, setCreateUserId] = useState<number>();
  const contactListId: number[] = [];
  const [disabled, setDisabled] = useState(true);
  const activityState = useActivityState();
  const riskAssessmentState = useRiskAssessmentState({ mode });
  const { initialData } = useActivityInitialData(false);
  const { riskAssessmentInitial } = useRiskAssessmentInitialData(true);
  const { traceabilityInitial } = useTraceabilityInitialData();
  const actionState = useActionState();
  const traceabilityState = useTraceabilityState();
  const [saved, setSaved] = useState(false);
  const { user } = useUser();

  useEffect(() => {
    if (activity) {
      setCreateUserId(activity?.createUser?.id);
      const { contacts } = activity;
      contacts.map(contact => {
        contactListId.push(contact.person.id);
      });
      if (
        activity.riskAssessment &&
        !activity.riskAssessment.riskAssessmentContainer.workerTrainingInformation
      ) {
        activity.riskAssessment.riskAssessmentContainer.workerTrainingInformation = {
          trainingCourse: false,
          other: false,
          otherTxt: '',
        };
      }

      updateActivityStateFromActivity(activity, activityState, mode);
      if (activity.riskAssessment) {
        updateRiskAssessmentStateFromRisk(activity.riskAssessment, riskAssessmentState);
      }
      if (activity.riskAssessment?.actions) {
        updateActionStateFromAction(activity.riskAssessment?.actions, actionState);
      }
      if (activity.riskAssessment?.activityTraceabilities) {
        updateTraceabilityStateFromTraceability(
          activity.riskAssessment.activityTraceabilities,
          traceabilityState
        );
      }
    }
  }, [activity, mode, id]);

  useEffect(() => {
    setIsLoading(isFetchingActivity);
  }, [isFetchingActivity]);

  useEffect(() => {
    if (saved && !isLoading) {
      history.push(`/${QueryKey.ACTIVITY}/${VIEW_MODE}/${id}`);
    }
  }, [saved, isLoading]);

  useEffect(() => {
    if (mode === EDIT_MODE || mode === CLONE_MODE) {
      setDisabled(false);
    } else if (mode === VIEW_MODE) {
      setDisabled(true);
    }
  }, [mode]);

  const renderButtonGroup = () => {
    return (
      <OptionsButtonGroup
        entityResponsible={contactListId}
        entityCreator={createUserId}
        history={history}
        id={id}
        mode={mode}
        activityState={activityState}
        riskAssessmentState={riskAssessmentState}
        riskAssessment={activity?.riskAssessment ?? riskAssessmentInitial}
        actionState={actionState}
        actions={actionState.actionCardContentList}
        activityTraceabilities={
          activity?.riskAssessment?.activityTraceabilities ?? traceabilityInitial
        }
        traceabilityState={traceabilityState}
        methods={methods}
        setIsLoading={setIsLoading}
        setSaved={setSaved}
      />
    );
  };

  const redirectIfNotEditAccess = () => {
    const redirect = mode === EDIT_MODE && user && !initSelectors.isActivityCanCreate(user, entityType.activity);
    if(redirect){
      history.replace('/forbidden');
      history.go(0);
    }
  }

  if (isLoading) {
    return <Loading isLoading />;
  }

  redirectIfNotEditAccess();

  return (
    <>
      <FormProvider {...methods}>
        <Form id="activityForm">
          {renderButtonGroup()}
          <div className={styles.buttonGroupAndTitle}>
            <TitleContainer
              header={
                activity?.title || t('activity.details.page.activity.details.container.title')
              }
              overview
            />
          </div>
          <div className={styles.activityContainerOverview}>
            <div className={styles.componentOverview}>
              <ActivityDetailsContainer
                mode={mode}
                disabled={disabled}
                activity={activityState}
                initialData={initialData}
              />
              <div className={styles.overviewComponents}>
                <ActionFormContainer
                  actionState={actionState}
                  initialData={initialData}
                  disabled={!(!disabled && activity?.status == ActivityStatus.CREATION)}
                />
              </div>
              <div className={styles.overviewComponents}>
                <TraceabilityFormContainer
                  traceabilityState={traceabilityState}
                  disabled={disabled}
                  initialData={initialData}
                  locations={activity?.location?.map(l => l.location)}
                  mode={mode}
                />
              </div>
            </div>
            <div className={styles.componentOverview}>
              <RiskAssessmentDetailsContainer
                riskAssessment={riskAssessmentState}
                disabledComponent={disabled}
                mode={mode}
              />
            </div>
          </div>
          {renderButtonGroup()}
        </Form>
      </FormProvider>
    </>
  );
};

const mapStateToProps = (state: any, ownProps: any) => ({
  id: ownProps.match.params.id,
  mode: ownProps.match.params.mode,
});

export default compose(
  withTranslation(),
  connect(mapStateToProps, null)
)(ViewEditAcivity) as React.FC<Props>;
